import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type ActionResult } from '@/lib/api'
import { centeredSwal, showError } from '@/lib/alert'
import { usePageTitle } from '@/hooks/usePageTitle'
import { PasswordInput } from '@/components/PasswordInput'
import { Preloader } from '@/components/Preloader'

/**
 * Port of Views/Account/ResetPassword.cshtml +
 * wwwroot/js/app/account/resetPassword.js.
 *
 * The email is NOT taken from the URL. AccountController stashed it in
 * TempData["ResetEmail"] when the OTP was confirmed and the view rendered it
 * into a hidden field; the equivalent here is GET account/reset-context, which
 * reads it back out of the session. That is what stops someone opening
 * /Account/ResetPassword?email=<someone-else> and resetting another account.
 *
 * The original also accepted `?email=` from the query string as a fallback
 * (resetPassword.js:36-38) — exactly that hole. It is not carried over: the
 * session value is the only source.
 *
 * `Token` is dropped from the payload. It was posted as a field, but
 * ResetPasswordRequestDto has no Token member, so ASP.NET discarded it — the
 * hash never authorised anything.
 */
const REQUIREMENTS = [
  { id: 'lengthReq', label: 'At least 8 characters', test: (v: string) => v.length >= 8 },
  { id: 'upperReq', label: 'One uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { id: 'lowerReq', label: 'One lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { id: 'numberReq', label: 'One number', test: (v: string) => /\d/.test(v) },
  {
    id: 'specialReq',
    label: 'One special character',
    test: (v: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(v),
  },
]

export function ResetPassword(): JSX.Element {
  usePageTitle('Reset Password', 'auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const hash = searchParams.get('hash') ?? ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['account', 'reset-context', hash],
    queryFn: () =>
      api.get<{ success: boolean; message?: string; email: string }>(
        `/api/ecareer/account/reset-context?hash=${encodeURIComponent(hash)}`,
      ),
    retry: false,
  })

  const email = data?.email ?? ''

  /**
   * "Email not found. Please start the password reset process again." — the
   * original waited 3s then sent the user back to ForgotPassword. Same here,
   * once the lookup has actually finished.
   */
  useEffect(() => {
    if (isLoading || !data) return
    if (data.success && email) return

    void (async () => {
      await showError('Email not found. Please start the password reset process again.')
      navigate('/Account/ForgotPassword')
    })()
  }, [isLoading, data, email, navigate])

  const meetsAllRequirements = REQUIREMENTS.every((requirement) => requirement.test(newPassword))
  const passwordsMatch = confirmPassword === '' || newPassword === confirmPassword

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault()

    if (!meetsAllRequirements) {
      await showError('Password does not meet the required criteria.')
      return
    }
    if (newPassword !== confirmPassword) {
      await showError('Passwords do not match.')
      return
    }
    if (!newPassword || !confirmPassword) {
      await showError('Please fill in all required fields.')
      return
    }
    if (!email) {
      await showError('Email not found. Please start the password reset process again.')
      return
    }

    setIsSubmitting(true)
    try {
      // Email is not sent: the server uses the address whose OTP this browser
      // confirmed, so a client cannot reset a different account.
      const result = await api.post<ActionResult>('/api/ecareer/account/reset-password', {
        NewPassword: newPassword,
        ConfirmPassword: confirmPassword,
      })

      if (!result.success) {
        await showError(result.message ?? 'Failed to reset password. Please try again.')
        return
      }

      await centeredSwal({
        icon: 'success',
        title: 'Password Reset Successfully!',
        text: 'Your password has been reset. You can now log in with your new password.',
        timer: 3000,
        showConfirmButton: false,
        allowOutsideClick: false,
        timerProgressBar: true,
      })
      navigate('/Account/Login')
    } catch (error) {
      await showError(
        error instanceof Error ? error.message : 'Failed to reset password. Please try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) return <Preloader />

  return (
    <>
      <div className="text-center">
        <img
          src="/lib/images/authentication/img-auth-reset-password.png"
          alt="Reset Password"
          className="img-fluid mb-3"
        />
        <h4 className="f-w-500 mb-1">Reset password</h4>
        <p className="mb-3">
          Back to{' '}
          <Link to="/Account/Login" className="link-primary ms-1">
            Log in
          </Link>
        </p>
      </div>

      <form id="resetPasswordForm" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className="mb-3">
          <label className="form-label">Password</label>
          <PasswordInput
            id="NewPassword"
            value={newPassword}
            onChange={setNewPassword}
            placeholder="Password"
            required
            autoComplete="new-password"
          />
          {/*
            resetPassword.js drove a `#lengthReq`/`#upperReq`/... checklist that
            ResetPassword.cshtml never rendered, so the rules were enforced but
            invisible — users saw only "does not meet the required criteria".
            The checklist is rendered here because the code to drive it already
            existed and the rules are unchanged; nothing new is being enforced.
          */}
          {newPassword.length > 0 && (
            <ul className="list-unstyled small mt-2 mb-0">
              {REQUIREMENTS.map((requirement) => {
                const isValid = requirement.test(newPassword)
                return (
                  <li
                    key={requirement.id}
                    id={requirement.id}
                    className={isValid ? 'text-success' : 'text-muted'}
                  >
                    <i className={`ti ${isValid ? 'ti-check' : 'ti-x'} requirement-icon me-1`} />
                    {requirement.label}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label">Confirm Password</label>
          <PasswordInput
            id="ConfirmPassword"
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="Confirm Password"
            required
            autoComplete="new-password"
            className={`form-control${passwordsMatch ? '' : ' is-invalid'}`}
          />
        </div>

        <div className="d-grid mt-4">
          <button
            type="submit"
            className="btn btn-primary"
            id="resetPasswordBtn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </div>
      </form>
    </>
  )
}
